import { useEffect, useState } from 'react';
import { Settings, Check, X } from 'lucide-react';
import { useTheme, type ThemeCategory } from '../contexts/ThemeContext';

const CATEGORY_ORDER: ThemeCategory[] = ['Standard', 'Dark', 'Accessibility', 'Color blind'];

/**
 * Settings gear in the header → opens a themed modal listing all 11
 * ctAS themes grouped by category. Selection is applied immediately
 * and persisted via ThemeContext (localStorage['pnb-theme']).
 */
export default function ThemePicker() {
  const { theme, themes, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="scpa-ghost-btn"
        title="Theme & appearance"
        aria-label="Theme & appearance"
      >
        <Settings className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div
          className="scpa-scrim"
          role="dialog"
          aria-modal="true"
          aria-label="Choose a theme"
          onClick={() => setOpen(false)}
        >
          <div className="scpa-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="section-title">Appearance</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted hover:text-fg"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[13px] text-muted mb-3">
              Pick a theme. It applies instantly and is remembered on this device.
            </p>

            {CATEGORY_ORDER.map(cat => {
              const group = themes.filter(t => t.category === cat);
              if (!group.length) return null;
              return (
                <div key={cat} className="mb-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted mb-2">{cat}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {group.map(t => {
                      const active = t.id === theme;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTheme(t.id)}
                          className="flex items-center gap-2.5 rounded-md border px-3 py-2 text-left text-[13px] transition-colors"
                          style={{
                            background: active ? 'var(--scpa-primary-muted)' : 'var(--scpa-surface)',
                            borderColor: active ? 'var(--scpa-primary)' : 'var(--scpa-border)',
                            color: 'var(--scpa-text)',
                          }}
                        >
                          <span
                            className="w-4 h-4 rounded-full shrink-0 border"
                            style={{ background: t.swatch, borderColor: 'var(--scpa-border-light)' }}
                          />
                          <span className="flex-1 truncate">{t.label}</span>
                          {active && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--scpa-primary)' }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end pt-1">
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
